// Imports
import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import { useAppDispatch } from '../../../../../../../shareables/foundation/front-end/redux/hooks';
import apiActions from '../../../../../../../shareables/integrations/api/redux/actions';
import { GetNewsResponse } from '../../../../../../../shareables/types/api/main/website/homepage/news/get';
import parseAndSanitizeMarkdown from '../../../../../../../shareables/utils/markdown/parse-and-sanitize';
import striptags from 'striptags';
import { DateTime } from 'luxon';
import { GeneratedThemeProps } from '../../../../../../templates/template-style';
import { Link } from 'react-router-dom';
import BottomInfo from '../bottom-info';
import Tippy from '@tippyjs/react';
import tippyProps from '../../../../../../../shareables/foundation/front-end/utils/tippy-props';


// Styled components
const NewsArticlesWrapper = styled.div<{ theme: GeneratedThemeProps }>`
	display: flex;
	gap: 1rem;
	overflow-wrap: anywhere;
	
	@media (max-width: 47.9375rem) {
		flex-direction: column;
		gap: 1.5rem;
	}
`;

const NewsArticle = styled.article`
	> small {
		display: block;
		margin-top: -0.0625rem;
		margin-bottom: 0.125rem;
		font-size: 0.8125rem;
	}
	
	flex: 1;
`;

const ArticleHeading = styled.strong`
	display: block;
	
	display: -webkit-box;
	overflow-y: hidden;
	-webkit-line-clamp: 1;
	-webkit-box-orient: vertical;
	
	font-size: 0.9375rem;
`;

const NewsArticleContent = styled.div<{ theme: GeneratedThemeProps }>`
	display: -webkit-box;
	overflow-y: hidden;
	
	-webkit-line-clamp: 3;
	-webkit-box-orient: vertical;
	
	padding-bottom: 0.125rem;
`;


// Define the accepted props
interface Props {
	storybookNews?: GetNewsResponse;
}


// Function component
const HomepageNewsArticles: React.FC<Props> = ({ storybookNews }) => {
	// Use state
	const [newsArticles, setNewsArticles] = useState<GetNewsResponse | null>(storybookNews ?? null);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Load homepage news on mount
	useEffect(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetNewsResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/homepage/news`,
				completion: (json) => {
					setNewsArticles(json.response);
				},
			})
		);
	}, [dispatch]);
	
	
	// If loading, then display nothing
	if (newsArticles === null) {
		return null;
	}
	
	
	// Return JSX
	return (
		<BottomInfo>
			<h3>Recent news</h3>
			<NewsArticlesWrapper>
				{newsArticles?.map((newsArticle) => {
					// Parse and sanitize news article content
					const newsArticleTextHTML = parseAndSanitizeMarkdown(newsArticle.text)
						// Replace tables with placeholder
						.replace(/<table[^>]*>[\s\S]*?<\/table>/gi, '<p>[table]</p>')
						// Replace images with placeholder
						.replace(/<img[^>]*>/gi, '<p>[photo]</p>')
						// Replace headings and make bold
						.replace(/<h[1-6][^>]*>/gi, '<p><strong>')
						.replace(/<\/h[1-6]>/gi, '</strong></p>')
						// Replace list items
						.replace(/<li>/gi, '<p>')
						.replace(/<\/li>/gi, '</p>');
					
					
					// Remove all but basic formatting tags
					const sanitizedContent = {
						__html: striptags(newsArticleTextHTML, ['strong', 'em', 'strike', 'a', 'p', 'br']),
					};
					
					
					// Return JSX
					return (
						<React.Fragment key={newsArticle.id}>
							<NewsArticle>
								<ArticleHeading>{newsArticle.title}</ArticleHeading>
								
								<small className='is-gray'>
									<Tippy
										{...tippyProps}
										content={new Date(newsArticle.published).toLocaleDateString(undefined, {
											month: 'long',
											day: 'numeric',
											year: 'numeric',
											hour: 'numeric',
											minute: '2-digit',
										})}
									>
										<span>{DateTime.fromISO(newsArticle.published).toRelative()}</span>
									</Tippy>
								</small>
								
								<NewsArticleContent dangerouslySetInnerHTML={sanitizedContent} />
								
								<Link
									to={`${newsArticle.uri}/article/${encodeURIComponent(
										newsArticle.title.replace(/\W+/g, ' ').replace(/ /g, '-')
									)}-${newsArticle.id}`}
									state={{ disableScrollToTop: true }}
								>
									Read full article
								</Link>
							</NewsArticle>
						</React.Fragment>
					);
				})}
			</NewsArticlesWrapper>
		</BottomInfo>
	);
};

export default HomepageNewsArticles;
